<template>
  <div class="coro-widget">
    <div
      v-if="widgetConfig.metadata.state === WidgetState.SETUP_REQUIRED"
      class="coro-widget__content coro-widget--empty"
      data-testid="device-widget-no-devices-container"
    >
      <v-icon class="empty-data-icon" :icon="`$${CoroIcons.EDR_S}`" />
      <div class="subtitle1 text-center">
        {{ $t(`dashboard.widgets.${widgetConfig.type}.noDevicesProtected`) }}
      </div>
      <div class="caption text-indigo-medium mb-4">
        {{ $t(`dashboard.widgets.${widgetConfig.type}.installCoroClients`) }}
      </div>
      <v-btn
        v-if="canManageDevicesSettings"
        :to="{ name: RouteName.DEVICES_SETTINGS_PAGE }"
        color="primary"
        rounded
        data-testid="edr-widget-add-protection-btn"
      >
        {{ $t(`dashboard.widgets.${widgetConfig.type}.addProtectionBtn`) }}
      </v-btn>
    </div>

    <div v-else-if="widgetDataValue.data" class="coro-widget__content">
      <div class="coro-widget__tile coro-widget__tile-content">
        <div class="mb-3">
          <div class="coro-widget-number text-left" data-testid="emails-widget-scanned-emails">
            {{ widgetDataValue.data.processesCount.toLocaleString() }}
          </div>
          <div class="body2">
            {{ $t(`dashboard.widgets.${widgetConfig.type}.processes`) }} /
            {{ $t(`dashboard.widgets.${widgetConfig.type}.last30Days`) }}
          </div>
        </div>
        <div v-if="widgetDataValue.data.topProcesses?.length" class="mb-3 mt-12">
          <div class="subtitle2 mb-4" data-testid="emails-widget-top-suspicious-domains">
            {{ $t(`dashboard.widgets.${widgetConfig.type}.topProcesses`) }}
          </div>
          <div
            v-for="process in widgetDataValue.data.topProcesses"
            :key="process.name"
            class="mb-4"
          >
            <div
              class="coro-link body2"
              :data-testid="`emails-widget-${kebabCase(process.name)}-link`"
              @click="viewProcess({ processName: process.name })"
            >
              {{ process.name }}
            </div>
            <div class="d-flex justify-start align-center">
              <coro-progress
                :value="process.percentage"
                :height="4"
                color="rgb(var(--v-theme-indigo-medium))"
              />
              <span class="ml-2 body2 text-primary">{{ process.count }}</span>
            </div>
          </div>
        </div>
      </div>
      <div class="coro-widget__tile">
        <vulnerabilities-tile
          :widget-data="widgetDataValue.data"
          @all-active-link-clicked="viewAllTickets(true, TicketsModuleFilter.EDR)"
          @all-caught-up-link-clicked="viewAllTickets(false, TicketsModuleFilter.EDR)"
          @vulnerability-clicked="viewVulnerability($event, TicketsModuleFilter.EDR)"
        />
        <generated-tickets-trend-chart
          v-if="widgetConfig.metadata.state === WidgetState.HAS_OPEN_TICKETS"
          class="pt-2 px-6"
          :chart-data="widgetDataValue.data.generatedTicketsHistory"
          :delta="widgetDataValue.data.generatedTicketsDelta"
        ></generated-tickets-trend-chart>
      </div>
    </div>

    <widget-error-state
      v-else
      :widget-type="widgetConfig.type"
      @retry-click="$emit('fetch-data')"
    />
  </div>
</template>

<script lang="ts">
import { TicketsModuleFilter, TicketStatus } from "@/constants/tickets";
import CoroProgress from "@/components/CoroProgress.vue";
import { computed, defineComponent, type PropType } from "vue";
import VulnerabilitiesTile from "@/components/VulnerabilitiesTile.vue";
import kebabCase from "lodash/kebabCase";
import { useRouter } from "vue-router";
import { getCurrentDate, getNDaysAgo } from "@/constants/date-range-picker";
import { useWidgetContent } from "@/composables/useWidgetContent";
import { type WidgetConfiguration, WidgetState } from "@/_helpers/dashboard.ts";
import GeneratedTicketsTrendChart from "@/components/dashboard/GeneratedTicketsTrendChart.vue";
import type { WidgetType } from "@/constants/dashboard.ts";
import WidgetErrorState from "@/components/dashboard/WidgetErrorState.vue";
import { RouteName } from "@/constants/routes.ts";
import { CoroIcons } from "@/constants/coro-icon.ts";
import { RolePermissionsScope, WorkspaceManagementScopeSections } from "@/constants/roles.ts";

export default defineComponent({
  props: {
    widgetConfig: {
      type: Object as PropType<WidgetConfiguration<WidgetType.EDR>>,
      required: true,
    },
  },
  components: {
    WidgetErrorState,
    GeneratedTicketsTrendChart,
    VulnerabilitiesTile,
    CoroProgress,
  },
  setup(props) {
    const router = useRouter();
    const widgetDataValue = computed(() => props.widgetConfig.data);

    const { viewVulnerability, viewAllTickets, isAccessRestricted } = useWidgetContent();

    const canManageDevicesSettings = computed(
      () =>
        !isAccessRestricted(
          RolePermissionsScope.WORKSPACE_MANAGEMENT,
          WorkspaceManagementScopeSections.DEVICES
        )
    );

    const viewProcess = (query: { processName: string }) => {
      router.push({
        name: RouteName.TICKETS,
        query: {
          from: "widget",
          status: TicketStatus.OPEN,
          widget: TicketsModuleFilter.EDR,
          startTime: getNDaysAgo(30),
          endTime: getCurrentDate(),
          search: `process-name:${query.processName}`,
        },
      });
    };

    return {
      widgetDataValue,
      viewProcess,
      viewVulnerability,
      viewAllTickets,
      isAccessRestricted,
      TicketsModuleFilter,
      WidgetState,
      RouteName,
      CoroIcons,
      canManageDevicesSettings,
      kebabCase,
    };
  },
});
</script>
